import {
  FORMAT_TEXT_COMMAND,
  UNDO_COMMAND,
  REDO_COMMAND,
  $getRoot,
  $createParagraphNode,
  $createTextNode,
  $getSelection,
} from 'lexical';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import styles from './RichTextEditor.module.scss';
import { Menu, Space } from 'antd';
import {
  AlignLeftOutlined,
  BgColorsOutlined,
  BoldOutlined,
  CodeOutlined,
  DeleteOutlined,
  FontColorsOutlined,
  FontSizeOutlined,
  ItalicOutlined,
  LinkOutlined,
  MenuOutlined,
  PlusOutlined,
  RedoOutlined,
  StrikethroughOutlined,
  UnderlineOutlined,
  UndoOutlined,
} from '@ant-design/icons';

import Dictaphone, { useDictaphone } from '../Dictaphone/Dictaphone';
import { useTranslation } from 'react-i18next';
import {
  useBlockTypeMenu,
  useFontFamilyMenu,
  useFontSize,
  useAddLinkModal,
  useFontColor,
  useTextTransformMenu,
  useInsertMenu,
  useAlignTypeMenu,
  ToolbarButton,
  ToolbarBlockTypeDropdownButton,
  ToolbarFontDropdownButton,
  ToolbarFontSizeInputButton,
  ToolbarAddLinkButton,
  ToolbarFontColorButton,
  ToolbarTextTransformDropdownButton,
  ToolbarInsertDropdownButton,
  ToolbarAlignDropdownButton,
  RichTextImageProps,
} from './Buttons';
import { useToolbarState } from './useToolbarState';

interface Props extends RichTextImageProps {}

export default function Toolbar(props: Props) {
  const [editor] = useLexicalComposerContext();
  const { t } = useTranslation();

  const { blockTypeMenu, selectedBlockType } = useBlockTypeMenu(editor);
  const toolbar = useToolbarState(editor);
  const { fontFamilyMenu, selectedFontFamily } = useFontFamilyMenu(editor);
  const fontSizeProps = useFontSize(editor);
  const addLinkProps = useAddLinkModal(editor);
  const fontColorProps = useFontColor(editor, 'self');
  const fontBackgroundColorProps = useFontColor(editor, 'background');
  const textTransformProps = useTextTransformMenu(editor);
  const insertMenuProps = useInsertMenu(editor, props);
  const alignMenuProps = useAlignTypeMenu(editor);
  const dictaphoneProps = useDictaphone();
  const dictaphoneId = 'dictaphone-richText';

  return (
    <div className={styles.toolbar}>
      <Space split="|">
        <div>
          <ToolbarButton
            icon={<UndoOutlined />}
            title={t('editor.undo.title')}
            onClick={() => editor.dispatchCommand(UNDO_COMMAND, undefined)}
          />
          <ToolbarButton
            icon={<RedoOutlined />}
            title={t('editor.redo.title')}
            onClick={() => editor.dispatchCommand(REDO_COMMAND, undefined)}
          />
        </div>

        <ToolbarBlockTypeDropdownButton
          icon={<MenuOutlined />}
          overlay={<Menu items={blockTypeMenu} />}
          title={t('editor.blockType.title')}
          selected={selectedBlockType}
        />

        <ToolbarFontDropdownButton
          icon={<FontSizeOutlined />}
          overlay={<Menu items={fontFamilyMenu} />}
          selected={selectedFontFamily}
          title={t('editor.fontFamily.title')}
        />

        <ToolbarFontSizeInputButton
          {...fontSizeProps}
          title={t('editor.fontSize.title')}
        />

        <div>
          <ToolbarButton
            icon={<BoldOutlined />}
            title={t('editor.bold.title')}
            onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')}
            active={toolbar.isBold}
          />
          <ToolbarButton
            icon={<ItalicOutlined />}
            title={t('editor.italic.title')}
            onClick={() =>
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')
            }
            active={toolbar.isItalic}
          />
          <ToolbarButton
            icon={<UnderlineOutlined />}
            title={t('editor.underline.title')}
            onClick={() =>
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline')
            }
            active={toolbar.isUnderline}
          />
          {false && (
            <ToolbarButton
              icon={<CodeOutlined />}
              title={t('editor.code.title')}
              onClick={() =>
                editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'code')
              }
              active={toolbar.isCode}
            />
          )}
          <ToolbarAddLinkButton
            {...addLinkProps}
            icon={<LinkOutlined />}
            title={t('editor.link.title')}
          />

          <ToolbarFontColorButton
            {...fontColorProps}
            title={t('editor.fontColor.title')}
            icon={<FontColorsOutlined />}
          />

          <ToolbarFontColorButton
            {...fontBackgroundColorProps}
            title={t('editor.backgroundColor.title')}
            icon={<BgColorsOutlined />}
          />

          <ToolbarTextTransformDropdownButton
            overlay={<Menu items={textTransformProps.textTransformMenu} />}
            icon={<StrikethroughOutlined />}
            title={t('editor.transform.title')}
          />

          <ToolbarInsertDropdownButton
            {...insertMenuProps}
            overlay={<Menu items={insertMenuProps.insertMenu} />}
            icon={<PlusOutlined />}
            title={t('editor.insert.title')}
            visibleText={t('editor.insert.title')}
          />

          <ToolbarAlignDropdownButton
            {...alignMenuProps}
            overlay={<Menu items={alignMenuProps.blockTypeMenu} />}
            icon={<AlignLeftOutlined />}
            title={t('editor.align.tile')}
          />
        </div>

        <div>
          <ToolbarButton
            icon={<DeleteOutlined />}
            title={t('editor.clear.title')}
            onClick={() =>
              editor.update(() => {
                const root = $getRoot();
                root.clear();
                root.selectStart();
              })
            }
          />
        </div>

        <div>
          <ToolbarButton
            icon={
              <Dictaphone
                id={dictaphoneId}
                activeVoiceId={dictaphoneProps.activeVoiceId}
                setActiveVoiceId={dictaphoneProps.setActiveVoiceId}
                onTranscript={(text) => {
                  editor.update(() => {
                    const paragraph = $createParagraphNode();
                    paragraph.append($createTextNode(text));

                    const selection = $getSelection();
                    if (selection) {
                      selection.insertNodes([paragraph]);
                    }
                  });
                }}
              />
            }
            title={t('editor.speechToText.title')}
          />
        </div>
      </Space>
    </div>
  );
}
